import React, { useEffect, useState } from 'react';
import useGenerateInvestmentMemo, { GenerateInvestmentMemoResponse } from 'queries/OpenAI/useGenerateInvestmentMemo';
import { useDebounce } from 'hooks/useDebounce';
import {
    AddressFields,
    CoordinateFields,
    GPTCheckboxItem,
    InvestmentMemoFormData,
    SelectedProFormaOption,
} from '../types';

export interface AISuggestions {
    highlights: GPTCheckboxItem[];
    drivers: GPTCheckboxItem[];
    fundamentals: GPTCheckboxItem[];
}

interface UseGenerateInvestmentMemoContentProps {
    selectedProFormaOption: SelectedProFormaOption | undefined;
    organizationId: string;
    isTitleAddressLoaded: boolean;
    addressFields: AddressFields;
    coordinateFields: CoordinateFields;
    setFormData: React.Dispatch<React.SetStateAction<InvestmentMemoFormData>>;
}

export const useGenerateInvestmentMemoContent = ({
    selectedProFormaOption,
    organizationId,
    isTitleAddressLoaded,
    addressFields,
    coordinateFields,
    setFormData,
}: UseGenerateInvestmentMemoContentProps) => {
    const { mutate: generateContent, isPending: isGenerating } = useGenerateInvestmentMemo();
    const [aiSuggestions, setAiSuggestions] = useState<AISuggestions>({
        highlights: [],
        drivers: [],
        fundamentals: [],
    });

    const debouncedAddressFields = useDebounce(addressFields, 2000);
    const debouncedCoordinateFields = useDebounce(coordinateFields, 2000);

    useEffect(() => {
        if (!selectedProFormaOption || !isTitleAddressLoaded) {
            return;
        }

        // In coordinates mode we need a valid lat/lng pair for the LLM to derive
        // proximity drivers and market fundamentals; in address mode we let the
        // existing address-debounce trigger handle empty fields.
        if (
            debouncedCoordinateFields.useCoordinates
            && (debouncedCoordinateFields.latitude == null || debouncedCoordinateFields.longitude == null)
        ) {
            return;
        }

        generateContent(
            {
                organizationId,
                dealId: selectedProFormaOption.value.dealId,
                proFormaId: selectedProFormaOption.value.id,
                addressFields: debouncedAddressFields,
                coordinateFields: debouncedCoordinateFields,
            },
            {
                onSuccess: (response: GenerateInvestmentMemoResponse) => {
                    console.log('AI Response:', response);

                    const aiHighlights = response.investmentHighlights.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    const aiDrivers = response.locationProximityDrivers.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    const aiFundamentals = response.marketOverviewFundamentals.map(item => ({
                        id: crypto.randomUUID(),
                        name: item.header,
                        description: item.description,
                        isChecked: true,
                        isGenerated: true,
                    }));

                    setAiSuggestions({
                        highlights: aiHighlights,
                        drivers: aiDrivers,
                        fundamentals: aiFundamentals,
                    });

                    setFormData(prev => ({
                        ...prev,
                        title: {
                            ...prev.title,
                            tagline: response.tagline,
                        },
                        executiveSummary: {
                            ...prev.executiveSummary,
                            opportunityDescription: response.opportunity,
                        },
                        location: {
                            ...prev.location,
                            drivers: aiDrivers.filter(d => d.isChecked),
                        },
                    }));
                },
                onError: error => {
                    console.error('Error generating content:', error);
                },
            },
        );
    }, [
        selectedProFormaOption,
        organizationId,
        debouncedAddressFields,
        debouncedCoordinateFields,
        isTitleAddressLoaded,
        generateContent,
        setFormData,
        setAiSuggestions,
    ]);

    return { isGenerating, aiSuggestions };
};
